import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCurrency } from "@/lib/utils";
import {
  isMultiCurrency,
  calculateQuoteLineAmounts,
  overrideDisplayAmount,
  type CurrencyCode,
} from "@forgepoint/shared";
import { type QuoteCurrencyFields } from "@/lib/quoteCurrency";

interface LineTotalCellProps {
  /** Line total in the editor's base currency, or null when not yet priced. */
  lineTotal: string | null;
  /** Derived base-currency unit price behind `lineTotal` (non-override lines). */
  unitPrice?: string | null;
  /** Quantity paired with `unitPrice`. */
  quantity?: string | null;
  /** Manual override in the quote's display currency, applied verbatim. */
  overrideUnitPrice?: string | null;
  /** Backend-stamped display-currency amount, preferred when still fresh. */
  convertedAmount?: string | null;
  /** When true, show a skeleton prompting the user to save to recalculate. */
  shouldShowSkeleton: boolean;
  /** Base currency the line total is denominated in. */
  currencyCode?: CurrencyCode;
  /** Quote currency context; when multi-currency, the converted amount shows on hover. */
  currencyContext?: QuoteCurrencyFields;
}

/**
 * Renders an item line total. For multi-currency quotes the display-currency
 * amount is the primary display - the backend-stamped `convertedAmount` when
 * fresh, else derived live via the shared calculations (a document-currency
 * override multiplies out verbatim, never converted from base) - with the
 * base-currency amount shown in a tooltip on hover.
 * @param props - line total, skeleton flag, base currency, and currency context
 * @returns the line total cell content
 */
export function LineTotalCell({
  lineTotal,
  unitPrice,
  quantity,
  overrideUnitPrice,
  convertedAmount,
  shouldShowSkeleton,
  currencyCode,
  currencyContext,
}: LineTotalCellProps) {
  if (shouldShowSkeleton) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex justify-end">
              <Skeleton className="h-4 w-10 sm:h-5 sm:w-16" />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>Save your updates to recalculate prices</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (!lineTotal) {
    return <span className="text-xs sm:text-sm text-muted-foreground">-</span>;
  }

  if (!currencyContext || !isMultiCurrency(currencyContext)) {
    return (
      <span className="text-xs sm:text-sm font-medium tabular-nums">
        {formatCurrency(lineTotal, currencyCode)}
      </span>
    );
  }

  // Multi-currency: show the quote-currency amount (symbol prefix, no code
  // suffix), preferring the backend-stamped value, with the base-currency
  // amount on hover.
  const converted =
    convertedAmount ??
    (overrideUnitPrice && quantity
      ? overrideDisplayAmount(overrideUnitPrice, quantity, currencyContext)
      : unitPrice && quantity
        ? calculateQuoteLineAmounts(unitPrice, quantity, currencyContext).amount
        : calculateQuoteLineAmounts(lineTotal, "1", currencyContext).amount);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-xs sm:text-sm font-medium tabular-nums cursor-help underline decoration-dotted decoration-muted-foreground/40 underline-offset-4">
            {formatCurrency(
              converted,
              currencyContext.quoteCurrency ?? undefined,
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p>{formatCurrency(lineTotal, currencyCode)}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
